import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchCampaign,
  fetchCampaignTargets,
  transitionCampaign,
  updateCampaign,
  deleteCampaign,
  addCampaignTargets,
  skipCampaignTarget,
  fetchCampaignEngagements,
  addCampaignEngagement,
  STATE_BADGE_COLOR,
  type CampaignState,
} from "../../api/campaigns";
import { ApiError } from "../../lib/api";
import { fetchTargets, type TargetListItem } from "../../api/targets";
import { fetchEngagements } from "../../api/engagements";
import { fetchGroups, syncCampaignToGroup } from "../../api/groups";
import { GanttChart } from "./gantt/GanttChart";
import { EngagementPanel } from "./gantt/EngagementPanel";
import { ScrollableTable } from "../../components/ScrollableTable";
import { EmailResultsPanel } from "../../components/EmailResultsPanel";

export function CampaignDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: campData } = useQuery({
    queryKey: ["campaign", id],
    queryFn: () => fetchCampaign(id!),
    refetchInterval: 3000,
  });

  const { data: targetsData } = useQuery({
    queryKey: ["campaign-targets", id],
    queryFn: () => fetchCampaignTargets(id!),
  });

  const campaign = campData?.record;

  const { data: engagementsData } = useQuery({
    queryKey: ["campaign-engagements", id],
    queryFn: () => fetchCampaignEngagements(id!),
    refetchInterval: campaign?.state === "running" ? 3000 : false,
  });

  const targets = targetsData?.items ?? [];
  const engagements = engagementsData?.items ?? [];
  const poolSize = targets.length;

  const [selectedSort, setSelectedSort] = useState<number | null>(null);

  const transitionMut = useMutation({
    mutationFn: (body: { target_state: string; halt_active_call?: boolean }) =>
      transitionCampaign(id!, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaign", id] });
      qc.invalidateQueries({ queryKey: ["campaign-targets", id] });
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 409) {
        if (err.detail === "campaign_missing_roe" || err.detail === "campaign_missing_window") {
          alert("Set ROE and window before activating.");
        } else {
          alert(`Transition failed: ${err.detail}`);
        }
      }
    },
  });

  const [showAddTargets, setShowAddTargets] = useState(false);
  const [showAddEngagement, setShowAddEngagement] = useState(false);

  // ROE + Window state — initialized lazily after campaign loads (see below)
  const [roeSignedBy, setRoeSignedBy] = useState("");
  const [roeSignedAt, setRoeSignedAt] = useState("");
  const [roeVersion, setRoeVersion] = useState(1);
  const [roeSha256, setRoeSha256] = useState("");
  const [winStart, setWinStart] = useState("");
  const [winEnd, setWinEnd] = useState("");
  const [winBizOnly, setWinBizOnly] = useState(true);
  const [winTz, setWinTz] = useState("America/Los_Angeles");
  const [winBizStart, setWinBizStart] = useState("09:00");
  const [winBizEnd, setWinBizEnd] = useState("18:00");
  // business_days: 0=Mon…4=Fri, 5=Sat, 6=Sun
  const [winBizDays, setWinBizDays] = useState<number[]>([0, 1, 2, 3, 4]);
  const [authInitialized, setAuthInitialized] = useState(false);

  if (!campaign) return <p style={{ color: "var(--text-muted)" }}>Loading...</p>;

  // Seed auth state from campaign once loaded
  if (!authInitialized) {
    if (campaign.roe_signed_by) setRoeSignedBy(campaign.roe_signed_by);
    if (campaign.roe_signed_at) {
      // Convert ISO → datetime-local format
      const d = new Date(campaign.roe_signed_at);
      if (!isNaN(d.getTime())) {
        const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
          .toISOString()
          .slice(0, 16);
        setRoeSignedAt(local);
      }
    }
    if (campaign.window_start) {
      const d = new Date(campaign.window_start);
      if (!isNaN(d.getTime())) {
        setWinStart(new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16));
      }
    }
    if (campaign.window_end) {
      const d = new Date(campaign.window_end);
      if (!isNaN(d.getTime())) {
        setWinEnd(new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16));
      }
    }
    if (campaign.window_business_hours_only !== null) setWinBizOnly(campaign.window_business_hours_only);
    if (campaign.window_timezone) setWinTz(campaign.window_timezone);
    if (campaign.window_business_hours_start) setWinBizStart(campaign.window_business_hours_start);
    if (campaign.window_business_hours_end) setWinBizEnd(campaign.window_business_hours_end);
    if (campaign.window_business_days) setWinBizDays(campaign.window_business_days);
    setAuthInitialized(true);
  }

  const state = campaign.state as CampaignState;
  const isEditable = state === "draft" || state === "active";
  const processed = campaign.targets_completed + campaign.targets_failed;
  const pending = campaign.targets_total - processed;

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <Link to="/campaigns" style={{ color: "var(--text-muted)", fontSize: 13, textDecoration: "none" }}>
            Campaigns
          </Link>
          <h1 style={{ fontFamily: "var(--font-display)", fontSize: 28, fontWeight: 600, color: "var(--text-primary)", margin: "4px 0 0" }}>
            {campaign.display_name}
          </h1>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <span style={{
            padding: "4px 14px",
            borderRadius: 12,
            fontSize: 13,
            fontWeight: 600,
            background: STATE_BADGE_COLOR[state] + "18",
            color: STATE_BADGE_COLOR[state],
            textTransform: "uppercase",
          }}>
            {state}
          </span>
          {state === "draft" && (
            <>
              <button onClick={() => transitionMut.mutate({ target_state: "active" })} style={btnStyle("var(--accent)")}>
                Activate
              </button>
              <button onClick={() => { if (confirm("Delete this draft campaign?")) { deleteCampaign(id!).then(() => navigate("/campaigns")); } }} style={btnStyle("var(--danger)")}>
                Delete
              </button>
            </>
          )}
          {state === "active" && (
            <button onClick={() => transitionMut.mutate({ target_state: "running" })} style={btnStyle("var(--success)")}>
              Start
            </button>
          )}
          {state === "running" && (
            <>
              <button onClick={() => transitionMut.mutate({ target_state: "paused" })} style={btnStyle("var(--warning)")}>
                Pause
              </button>
              <button onClick={() => transitionMut.mutate({ target_state: "cancelled", halt_active_call: true })} style={btnStyle("var(--danger)")}>
                Cancel
              </button>
            </>
          )}
          {state === "paused" && (
            <>
              <button onClick={() => transitionMut.mutate({ target_state: "running" })} style={btnStyle("var(--success)")}>
                Resume
              </button>
              <button onClick={() => transitionMut.mutate({ target_state: "cancelled" })} style={btnStyle("var(--danger)")}>
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {/* Description (if set) */}
      {campaign.description && (
        <p style={{ color: "var(--text-secondary)", fontSize: 14, margin: "0 0 20px" }}>{campaign.description}</p>
      )}

      {/* Config + Progress cards */}
      <div className="grid-2col-responsive" style={{ marginBottom: 24 }}>
        <Card title="Configuration">
          <Row label="Engagements">{campaign.engagement_count}</Row>
          <Row label="Created by">{campaign.created_by}</Row>
        </Card>

        <Card title="Progress">
          <div style={{ fontSize: 24, fontWeight: 600, color: "var(--text-primary)", marginBottom: 8 }}>
            {campaign.targets_completed} / {campaign.targets_total}
          </div>
          <div style={{ width: "100%", height: 8, borderRadius: 4, background: "var(--border)", overflow: "hidden", marginBottom: 8 }}>
            <div style={{ width: campaign.targets_total > 0 ? `${(campaign.targets_completed / campaign.targets_total) * 100}%` : "0%", height: "100%", background: "var(--success)", borderRadius: 4 }} />
          </div>
          <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>
            {campaign.targets_completed} completed, {campaign.targets_failed} failed, {pending} pending
          </div>
        </Card>
      </div>

      {/* Target Group link + re-sync */}
      <div style={{ marginBottom: 24 }}>
        <TargetGroupCard campaign={campaign} campaignId={id!} />
      </div>

      {/* Authorization & Window */}
      {isEditable && (
        <AuthCard
          campaign={campaign}
          campaignId={id!}
          roeSignedBy={roeSignedBy} setRoeSignedBy={setRoeSignedBy}
          roeSignedAt={roeSignedAt} setRoeSignedAt={setRoeSignedAt}
          roeVersion={roeVersion} setRoeVersion={setRoeVersion}
          roeSha256={roeSha256} setRoeSha256={setRoeSha256}
          winStart={winStart} setWinStart={setWinStart}
          winEnd={winEnd} setWinEnd={setWinEnd}
          winBizOnly={winBizOnly} setWinBizOnly={setWinBizOnly}
          winTz={winTz} setWinTz={setWinTz}
          winBizStart={winBizStart} setWinBizStart={setWinBizStart}
          winBizEnd={winBizEnd} setWinBizEnd={setWinBizEnd}
          winBizDays={winBizDays} setWinBizDays={setWinBizDays}
        />
      )}

      {/* Engagements section */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h2 style={{ fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 600, color: "var(--text-primary)", margin: 0 }}>
            Engagements ({engagements.length})
          </h2>
          {isEditable && (
            <button onClick={() => setShowAddEngagement(true)} style={btnStyle("var(--accent)")}>
              + Add Engagement
            </button>
          )}
        </div>

        {showAddEngagement && (
          <AddEngagementForm
            campaignId={id!}
            onClose={() => setShowAddEngagement(false)}
          />
        )}

        <GanttChart
          engagements={engagements}
          windowTz={campaign.window_timezone ?? "UTC"}
          businessDays={campaign.window_business_days ?? [0, 1, 2, 3, 4]}
          poolSize={poolSize}
          onSelect={setSelectedSort}
        />
        {selectedSort !== null && (() => {
          const sel = engagements.find((e) => e.sort_order === selectedSort);
          return sel ? (
            <EngagementPanel
              campaignId={id!}
              engagement={sel}
              editable={campaign.state === "draft" || campaign.state === "active" || sel.state === "scheduled"}
              onClose={() => setSelectedSort(null)}
            />
          ) : null;
        })()}
      </div>

      {/* Target List */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 600, color: "var(--text-primary)", margin: 0 }}>
          Targets ({targets.length})
        </h2>
        {(state === "draft" || state === "active") && (
          <button onClick={() => setShowAddTargets(true)} style={btnStyle("var(--accent)")}>
            + Add Targets
          </button>
        )}
      </div>

      {showAddTargets && (
        <AddTargetsModal campaignId={id!} onClose={() => { setShowAddTargets(false); qc.invalidateQueries({ queryKey: ["campaign-targets", id] }); qc.invalidateQueries({ queryKey: ["campaign", id] }); }} />
      )}

      <ScrollableTable minWidth={440}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--border)", textAlign: "left" }}>
              <th style={thStyle}>#</th>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Phone</th>
              {(state === "draft" || state === "active") && <th style={thStyle}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {targets.map((t) => (
              <tr key={t.sort_order} style={{
                borderBottom: "1px solid var(--border-subtle)",
                background: "transparent",
              }}>
                <td style={tdStyle}>{t.sort_order + 1}</td>
                <td style={{ ...tdStyle, fontWeight: 500 }}>{t.target_display_name}</td>
                <td style={{ ...tdStyle, fontFamily: "var(--font-mono)", fontSize: 12 }}>{t.target_phone_e164}</td>
                {(state === "draft" || state === "active") && (
                  <td style={tdStyle}>
                    <SkipButton campaignId={id!} sortOrder={t.sort_order} />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollableTable>

      <div style={{ marginTop: 24 }}>
        <EmailResultsPanel campaignId={id!} live={campaign.state === "running"} />
      </div>
    </div>
  );
}

// ─── Add Engagement Form ────────────────────────────────────────────────────────

function AddEngagementForm({ campaignId, onClose }: { campaignId: string; onClose: () => void }) {
  const qc = useQueryClient();

  const { data: engData } = useQuery({
    queryKey: ["engagements"],
    queryFn: fetchEngagements,
  });

  // Templates are dispatchable regardless of their own state — compose_dispatch_engagement
  // overrides state to PENDING + re-activates fresh at dispatch. So filter by is_template,
  // excluding terminal states (revoked/concluded) as a sanity guard.
  const templateEngagements = (engData?.items ?? []).filter(
    (e) => e.is_template && e.state !== "revoked" && e.state !== "concluded",
  );

  const [engId, setEngId] = useState("");
  const [schedStart, setSchedStart] = useState("");
  const [schedEnd, setSchedEnd] = useState("");
  const [dailyStart, setDailyStart] = useState("09:00");
  const [dailyEnd, setDailyEnd] = useState("18:00");
  const [staggerPattern, setStaggerPattern] = useState("even_spread");
  const [staggerInterval, setStaggerInterval] = useState(60);
  const [staggerJitter, setStaggerJitter] = useState(0);

  const addMut = useMutation({
    mutationFn: () =>
      addCampaignEngagement(campaignId, {
        engagement_id: engId,
        ...(schedStart ? { scheduled_start_at: new Date(schedStart).toISOString() } : {}),
        ...(schedEnd ? { scheduled_end_at: new Date(schedEnd).toISOString() } : {}),
        active_daily_start: dailyStart,
        active_daily_end: dailyEnd,
        stagger_pattern: staggerPattern,
        ...(staggerPattern === "fixed_interval" ? { stagger_interval_seconds: staggerInterval } : {}),
        stagger_jitter_seconds: staggerJitter,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaign-engagements", campaignId] });
      qc.invalidateQueries({ queryKey: ["campaign", campaignId] });
      onClose();
    },
  });

  const inputSt: React.CSSProperties = {
    padding: "6px 10px",
    borderRadius: "var(--radius-input)",
    border: "1px solid var(--border)",
    background: "var(--bg-input)",
    color: "var(--text-primary)",
    fontSize: 13,
    width: "100%",
    boxSizing: "border-box",
  };
  const labelSt: React.CSSProperties = {
    fontSize: 11,
    color: "var(--text-muted)",
    display: "block",
    marginBottom: 3,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  };

  return (
    <div style={{
      background: "var(--bg-card)",
      border: "1px solid var(--border)",
      borderRadius: "var(--radius-card)",
      padding: 20,
      marginBottom: 16,
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 14, textTransform: "uppercase", letterSpacing: "0.04em" }}>
        Add Engagement
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12, marginBottom: 14 }}>
        <div style={{ gridColumn: "span 2" }}>
          <span style={labelSt}>Engagement template</span>
          <select value={engId} onChange={(e) => setEngId(e.target.value)} style={inputSt}>
            <option value="">Select...</option>
            {templateEngagements.map((e) => (
              <option key={e.engagement_id} value={e.engagement_id}>
                {e.display_name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <span style={labelSt}>Scheduled start</span>
          <input type="datetime-local" value={schedStart} onChange={(e) => setSchedStart(e.target.value)} style={inputSt} />
        </div>
        <div>
          <span style={labelSt}>Scheduled end</span>
          <input type="datetime-local" value={schedEnd} onChange={(e) => setSchedEnd(e.target.value)} style={inputSt} />
        </div>
        <div>
          <span style={labelSt}>Daily start</span>
          <input type="time" value={dailyStart} onChange={(e) => setDailyStart(e.target.value)} style={inputSt} />
        </div>
        <div>
          <span style={labelSt}>Daily end</span>
          <input type="time" value={dailyEnd} onChange={(e) => setDailyEnd(e.target.value)} style={inputSt} />
        </div>
        <div>
          <span style={labelSt}>Stagger pattern</span>
          <select value={staggerPattern} onChange={(e) => setStaggerPattern(e.target.value)} style={inputSt}>
            <option value="even_spread">Even spread</option>
            <option value="fixed_interval">Fixed interval</option>
          </select>
        </div>
        {staggerPattern === "fixed_interval" && (
          <div>
            <span style={labelSt}>Interval (seconds)</span>
            <input
              type="number"
              min={1}
              value={staggerInterval}
              onChange={(e) => setStaggerInterval(parseInt(e.target.value, 10) || 60)}
              style={inputSt}
            />
          </div>
        )}
        <div>
          <span style={labelSt}>Jitter (seconds)</span>
          <input
            type="number"
            min={0}
            value={staggerJitter}
            onChange={(e) => setStaggerJitter(parseInt(e.target.value, 10) || 0)}
            style={inputSt}
          />
        </div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button
          onClick={() => addMut.mutate()}
          disabled={!engId || addMut.isPending}
          style={{ ...btnStyle("var(--accent)"), opacity: !engId ? 0.5 : 1 }}
        >
          {addMut.isPending ? "Adding…" : "Add engagement"}
        </button>
        <button onClick={onClose} style={btnStyle("var(--text-muted)")}>Cancel</button>
        {addMut.isError && (
          <span style={{ fontSize: 12, color: "var(--danger)", alignSelf: "center" }}>Failed to add engagement</span>
        )}
      </div>
    </div>
  );
}

// ─── Target Group link + re-sync ────────────────────────────────────────────────
//
// Groups are frozen once a campaign leaves DRAFT (backend returns 409
// "campaign_started_group_frozen"), so the picker + re-sync are only live while
// state === "draft".

function TargetGroupCard({
  campaign,
  campaignId,
}: {
  campaign: {
    customer_id: string;
    state: CampaignState;
    target_group_id?: string | null;
  };
  campaignId: string;
}) {
  const qc = useQueryClient();
  const frozen = campaign.state !== "draft";

  const { data: groupsData } = useQuery({
    queryKey: ["groups"],
    queryFn: fetchGroups,
  });
  // Groups are customer-scoped; only offer ones belonging to this campaign.
  const groups = (groupsData?.items ?? []).filter(
    (g) => g.customer_id === campaign.customer_id,
  );

  const linkMut = useMutation({
    mutationFn: (groupId: string | null) =>
      updateCampaign(campaignId, { target_group_id: groupId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaign", campaignId] });
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 409) {
        alert("Group is frozen once the campaign starts.");
      } else {
        alert("Failed to update target group.");
      }
    },
  });

  const syncMut = useMutation({
    mutationFn: () => syncCampaignToGroup(campaignId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaign-targets", campaignId] });
      qc.invalidateQueries({ queryKey: ["campaign", campaignId] });
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 409) {
        alert("Group is frozen once the campaign starts.");
      }
    },
  });

  const selectSt: React.CSSProperties = {
    padding: "6px 10px",
    borderRadius: "var(--radius-input)",
    border: "1px solid var(--border)",
    background: "var(--bg-input)",
    color: "var(--text-primary)",
    fontSize: 13,
    minWidth: 240,
    boxSizing: "border-box",
    opacity: frozen ? 0.5 : 1,
    cursor: frozen ? "not-allowed" : "pointer",
  };
  const labelSt: React.CSSProperties = {
    fontSize: 11,
    color: "var(--text-muted)",
    display: "block",
    marginBottom: 3,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  };

  const r = syncMut.data;

  return (
    <Card title="Target Group">
      <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div>
          <span style={labelSt}>Linked group</span>
          <select
            style={selectSt}
            disabled={frozen || linkMut.isPending}
            value={campaign.target_group_id ?? ""}
            onChange={(e) => linkMut.mutate(e.target.value || null)}
          >
            <option value="">— none —</option>
            {groups.map((g) => (
              <option key={g.group_id} value={g.group_id}>
                {g.name} ({g.member_count})
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={() => syncMut.mutate()}
          disabled={frozen || syncMut.isPending || !campaign.target_group_id}
          style={{
            ...btnStyle("var(--accent)"),
            opacity: frozen || syncMut.isPending || !campaign.target_group_id ? 0.5 : 1,
            cursor: frozen || !campaign.target_group_id ? "not-allowed" : "pointer",
          }}
        >
          {syncMut.isPending ? "Syncing…" : "Re-sync from group"}
        </button>
      </div>

      {r && (
        <div style={{ marginTop: 12, fontSize: 12, color: "var(--text-secondary)" }}>
          <strong style={{ color: "var(--success)" }}>+{r.added}</strong> added,{" "}
          <strong style={{ color: "var(--danger)" }}>−{r.removed}</strong> removed
          {(r.skipped_test_only > 0 || r.skipped_no_phone > 0 || r.skipped_missing > 0) && (
            <>
              {" "}· skipped: {r.skipped_test_only} test-only, {r.skipped_no_phone} no-phone,{" "}
              {r.skipped_missing} missing
            </>
          )}
        </div>
      )}
      {syncMut.isError && (
        <div style={{ marginTop: 12, fontSize: 12, color: "var(--danger)" }}>Sync failed.</div>
      )}

      <div style={{ marginTop: 12, fontSize: 12, color: "var(--text-muted)" }}>
        Group is frozen once the campaign starts.
      </div>
    </Card>
  );
}

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function AuthCard({
  campaign, campaignId,
  roeSignedBy, setRoeSignedBy,
  roeSignedAt, setRoeSignedAt,
  roeVersion, setRoeVersion,
  roeSha256, setRoeSha256,
  winStart, setWinStart,
  winEnd, setWinEnd,
  winBizOnly, setWinBizOnly,
  winTz, setWinTz,
  winBizStart, setWinBizStart,
  winBizEnd, setWinBizEnd,
  winBizDays, setWinBizDays,
}: {
  campaign: { roe_signed_by: string | null; roe_signed_at: string | null; window_start: string | null; window_end: string | null };
  campaignId: string;
  roeSignedBy: string; setRoeSignedBy: (v: string) => void;
  roeSignedAt: string; setRoeSignedAt: (v: string) => void;
  roeVersion: number; setRoeVersion: (v: number) => void;
  roeSha256: string; setRoeSha256: (v: string) => void;
  winStart: string; setWinStart: (v: string) => void;
  winEnd: string; setWinEnd: (v: string) => void;
  winBizOnly: boolean; setWinBizOnly: (v: boolean) => void;
  winTz: string; setWinTz: (v: string) => void;
  winBizStart: string; setWinBizStart: (v: string) => void;
  winBizEnd: string; setWinBizEnd: (v: string) => void;
  winBizDays: number[]; setWinBizDays: (v: number[]) => void;
}) {
  const qc = useQueryClient();
  const saveMut = useMutation({
    mutationFn: () => {
      const roeAllFilled = roeSignedBy && roeSignedAt && roeSha256;
      const winAllFilled = winStart && winEnd;
      return updateCampaign(campaignId, {
        ...(roeAllFilled ? {
          roe: {
            sha256: roeSha256,
            version: roeVersion,
            signed_by: roeSignedBy,
            signed_at: new Date(roeSignedAt).toISOString(),
          },
        } : {}),
        ...(winAllFilled ? {
          window: {
            start: new Date(winStart).toISOString(),
            end: new Date(winEnd).toISOString(),
            business_hours_only: winBizOnly,
            business_hours_timezone: winTz || undefined,
            business_hours_start: winBizStart || undefined,
            business_hours_end: winBizEnd || undefined,
            business_days: winBizDays.length > 0 ? winBizDays : undefined,
          },
        } : {}),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaign", campaignId] });
    },
  });

  const inputSt: React.CSSProperties = {
    padding: "4px 8px",
    borderRadius: "var(--radius-input)",
    border: "1px solid var(--border)",
    background: "var(--bg-input)",
    color: "var(--text-primary)",
    fontSize: 13,
    width: "100%",
    boxSizing: "border-box",
  };
  const labelSt: React.CSSProperties = {
    fontSize: 11,
    color: "var(--text-muted)",
    display: "block",
    marginBottom: 3,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  };

  const toggleDay = (d: number) => {
    setWinBizDays(winBizDays.includes(d) ? winBizDays.filter((x) => x !== d) : [...winBizDays, d].sort((a, b) => a - b));
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <Card title="Authorization &amp; Window">
        {/* Status line */}
        <div style={{ display: "flex", gap: 24, marginBottom: 16, flexWrap: "wrap" }}>
          <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
            <span style={{ color: "var(--text-muted)", marginRight: 4 }}>ROE:</span>
            {campaign.roe_signed_by
              ? <>signed by <strong style={{ color: "var(--text-primary)" }}>{campaign.roe_signed_by}</strong>{campaign.roe_signed_at ? ` on ${new Date(campaign.roe_signed_at).toLocaleDateString()}` : ""}</>
              : <span style={{ color: "var(--warning)" }}>No ROE set</span>}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
            <span style={{ color: "var(--text-muted)", marginRight: 4 }}>Window:</span>
            {campaign.window_start && campaign.window_end
              ? <>{new Date(campaign.window_start).toLocaleDateString()} → {new Date(campaign.window_end).toLocaleDateString()}</>
              : <span style={{ color: "var(--warning)" }}>No window set</span>}
          </div>
        </div>

        <div className="grid-2col-responsive" style={{ gap: 20 }}>
          {/* ROE section */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>ROE Document</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div>
                <span style={labelSt}>Signed by</span>
                <input style={inputSt} value={roeSignedBy} onChange={(e) => setRoeSignedBy(e.target.value)} placeholder="Operator name" />
              </div>
              <div>
                <span style={labelSt}>Signed at</span>
                <input style={inputSt} type="datetime-local" value={roeSignedAt} onChange={(e) => setRoeSignedAt(e.target.value)} />
              </div>
              <div>
                <span style={labelSt}>Version</span>
                <input style={{ ...inputSt, width: 80 }} type="number" min={1} value={roeVersion} onChange={(e) => setRoeVersion(parseInt(e.target.value, 10) || 1)} />
              </div>
              <div>
                <span style={labelSt}>SHA-256 digest</span>
                <input style={inputSt} value={roeSha256} onChange={(e) => setRoeSha256(e.target.value)} placeholder="64-char hex digest" maxLength={64} spellCheck={false} />
              </div>
            </div>
          </div>

          {/* Window section */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>Operational Window</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div>
                <span style={labelSt}>Start</span>
                <input style={inputSt} type="datetime-local" value={winStart} onChange={(e) => setWinStart(e.target.value)} />
              </div>
              <div>
                <span style={labelSt}>End</span>
                <input style={inputSt} type="datetime-local" value={winEnd} onChange={(e) => setWinEnd(e.target.value)} />
              </div>
              <div>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: "var(--text-primary)" }}>
                  <input type="checkbox" checked={winBizOnly} onChange={(e) => setWinBizOnly(e.target.checked)} />
                  Business hours only
                </label>
              </div>
              {winBizOnly && (
                <>
                  <div>
                    <span style={labelSt}>Timezone</span>
                    <input style={inputSt} value={winTz} onChange={(e) => setWinTz(e.target.value)} placeholder="America/Los_Angeles" />
                  </div>
                  <div className="grid-2col-responsive" style={{ gap: 8 }}>
                    <div>
                      <span style={labelSt}>Hours start</span>
                      <input style={inputSt} type="time" value={winBizStart} onChange={(e) => setWinBizStart(e.target.value)} />
                    </div>
                    <div>
                      <span style={labelSt}>Hours end</span>
                      <input style={inputSt} type="time" value={winBizEnd} onChange={(e) => setWinBizEnd(e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <span style={labelSt}>Business days</span>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {DAY_LABELS.map((label, idx) => (
                        <label key={idx} style={{
                          display: "flex", alignItems: "center", gap: 4, cursor: "pointer",
                          padding: "3px 8px", borderRadius: "var(--radius-input)",
                          border: `1px solid ${winBizDays.includes(idx) ? "var(--accent)" : "var(--border)"}`,
                          background: winBizDays.includes(idx) ? "color-mix(in srgb, var(--accent) 15%, transparent)" : "transparent",
                          fontSize: 12, color: winBizDays.includes(idx) ? "var(--accent)" : "var(--text-secondary)",
                        }}>
                          <input type="checkbox" checked={winBizDays.includes(idx)} onChange={() => toggleDay(idx)} style={{ display: "none" }} />
                          {label}
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <button
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending}
            style={{ ...btnStyle("var(--accent)"), opacity: saveMut.isPending ? 0.7 : 1 }}
          >
            {saveMut.isPending ? "Saving…" : "Save authorization"}
          </button>
          {saveMut.isSuccess && (
            <span style={{ marginLeft: 12, fontSize: 12, color: "var(--success)" }}>Saved</span>
          )}
          {saveMut.isError && (
            <span style={{ marginLeft: 12, fontSize: 12, color: "var(--danger)" }}>Save failed</span>
          )}
        </div>
      </Card>
    </div>
  );
}

function AddTargetsModal({ campaignId, onClose }: { campaignId: string; onClose: () => void }) {
  const { data } = useQuery({ queryKey: ["targets"], queryFn: () => fetchTargets() });
  const [selected, setSelected] = useState<string[]>([]);
  const addMut = useMutation({
    mutationFn: () => addCampaignTargets(campaignId, selected),
    onSuccess: onClose,
  });
  const allTargets = data?.items ?? [];
  const withPhone = allTargets.filter((t: TargetListItem) => t.primary_phone_e164);
  return (
    <div style={{
      background: "var(--bg-card)",
      border: "1px solid var(--border)",
      borderRadius: "var(--radius-card)",
      padding: 20,
      marginBottom: 20,
    }}>
      <div style={{ maxHeight: 300, overflow: "auto", marginBottom: 12 }}>
        {withPhone.map((t: TargetListItem) => (
          <label key={t.target_id} style={{ display: "flex", gap: 8, padding: "6px 0", cursor: "pointer", color: "var(--text-primary)" }}>
            <input
              type="checkbox"
              checked={selected.includes(t.target_id)}
              onChange={(e) =>
                setSelected((s) =>
                  e.target.checked ? [...s, t.target_id] : s.filter((x) => x !== t.target_id),
                )
              }
            />
            {t.full_name} ({t.primary_phone_e164})
          </label>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={() => addMut.mutate()} disabled={selected.length === 0} style={btnStyle("var(--accent)")}>
          Add {selected.length} target{selected.length !== 1 ? "s" : ""}
        </button>
        <button onClick={onClose} style={btnStyle("var(--text-muted)")}>Cancel</button>
      </div>
    </div>
  );
}

function SkipButton({ campaignId, sortOrder }: { campaignId: string; sortOrder: number }) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => skipCampaignTarget(campaignId, sortOrder),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaign-targets", campaignId] });
    },
  });
  return (
    <button onClick={() => mut.mutate()} style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 12, textDecoration: "underline" }}>
      Skip
    </button>
  );
}


function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "var(--radius-card)", padding: 20 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--border-subtle)" }}>
      <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>{label}</span>
      <span style={{ fontSize: 13, color: "var(--text-primary)" }}>{children}</span>
    </div>
  );
}

function btnStyle(color: string): React.CSSProperties {
  return {
    background: color,
    color: "#fff",
    border: "none",
    borderRadius: "var(--radius-input)",
    padding: "6px 14px",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
  };
}

const thStyle: React.CSSProperties = { padding: "10px 12px", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em" };
const tdStyle: React.CSSProperties = { padding: "10px 12px", fontSize: 13, color: "var(--text-primary)" };
